import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { Text2xl, TextLg, TextMd, TextSm } from "../../components/text";
import sales_data from "../../assets/datasets/sales.json" with { type: "json" };
import { useAppContext } from "../../context/appContext";
import { ImageContainer } from "../../components/imageContainer";

const money = (value, compact = false) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 0,
  }).format(value || 0);

const number = (value) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value || 0);

const toNumber = (value) => Number(value || 0);

const groupSum = (data, key, valueKey) =>
  Object.entries(
    data.reduce((acc, item) => {
      const label = item[key] || "Unknown";
      acc[label] = (acc[label] || 0) + toNumber(item[valueKey]);
      return acc;
    }, {}),
  )
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

const groupQuarterlySales = (data) =>
  Object.entries(
    data.reduce((acc, item) => {
      const date = new Date(item["Order Date"]);

      if (Number.isNaN(date.getTime())) return acc;

      const year = date.getFullYear();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      const key = `${year} Q${quarter}`;

      acc[key] = (acc[key] || 0) + toNumber(item.Sales);

      return acc;
    }, {}),
  )
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => {
      const [yearA, quarterA] = a.name.split(" Q");
      const [yearB, quarterB] = b.name.split(" Q");

      if (yearA !== yearB) return Number(yearA) - Number(yearB);

      return Number(quarterA) - Number(quarterB);
    });

const LazyChart = ({ type, height, series, options }) => {
  const chartRef = useRef(null);
  const [viewed, setViewed] = useState(false);

  useEffect(() => {
    if (!chartRef.current || viewed) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setViewed(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.5,
        rootMargin: "-40px 0px -40px 0px",
      },
    );

    observer.observe(chartRef.current);

    return () => observer.disconnect();
  }, [viewed]);

  const emptySeries = useMemo(() => {
    if (["donut", "pie", "radialBar"].includes(type)) {
      return Array.isArray(series) ? series.map(() => 0) : [];
    }

    return series.map((item) => ({
      ...item,
      data: item.data.map(() => 0),
    }));
  }, [series, type]);

  return (
    <div ref={chartRef}>
      <Chart
        type={type}
        height={height}
        series={viewed ? series : emptySeries}
        options={options}
      />
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="relative overflow-hidden rounded-2xl bg-white/[0.08] border border-white/10 p-5 backdrop-blur-xl">
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-600/70 to-transparent" />

    <TextSm className="uppercase tracking-[0.12em] text-blue-600 font-semibold">
      {label}
    </TextSm>

    <TextLg className="font-bold text-white mt-3">{value}</TextLg>

    {sub && <TextSm className="text-slate-400 mt-1">{sub}</TextSm>}
  </div>
);

const ChartPanel = ({ title, subtitle, children, className = "" }) => (
  <div
    className={`
      rounded-3xl bg-white border border-slate-200/70
      shadow-[0_20px_60px_rgba(15,23,42,0.08)]
      p-6 lg:p-7 ${className}
    `}
  >
    <div className="mb-6">
      <TextMd className="font-bold text-slate-900">{title}</TextMd>

      {subtitle && <TextSm className="text-slate-500 mt-1">{subtitle}</TextSm>}
    </div>

    {children}
  </div>
);

export const Dashboard = () => {
  const dashboard = useMemo(() => {
    const totalSales = sales_data.reduce(
      (sum, item) => sum + toNumber(item.Sales),
      0,
    );

    const totalProfit = sales_data.reduce(
      (sum, item) => sum + toNumber(item.Profit),
      0,
    );

    const totalQuantity = sales_data.reduce(
      (sum, item) => sum + toNumber(item.Quantity),
      0,
    );

    const avgDiscount =
      sales_data.reduce((sum, item) => sum + toNumber(item.Discount), 0) /
      sales_data.length;

    return {
      totalSales,
      totalProfit,
      totalQuantity,
      avgDiscount,
      profitMargin: totalSales ? totalProfit / totalSales : 0,
      byRegion: groupSum(sales_data, "Region", "Sales"),
      byCategory: groupSum(sales_data, "Category", "Sales"),
      bySegment: groupSum(sales_data, "Segment", "Sales"),
      byShipMode: groupSum(sales_data, "Ship Mode", "Sales"),
      topSubCategories: groupSum(sales_data, "Sub-Category", "Sales").slice(
        0,
        8,
      ),
      quarterlySales: groupQuarterlySales(sales_data),
      recentOrders: [...sales_data]
        .sort((a, b) => new Date(b["Order Date"]) - new Date(a["Order Date"]))
        .slice(0, 8),
    };
  }, []);

  const chartBase = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "inherit",
      foreColor: "#64748b",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 900,
        animateGradually: {
          enabled: true,
          delay: 120,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 700,
        },
      },
    },
    colors: ["#2563eb", "#06b6d4", "#0ea5e9", "#1d4ed8"],
    dataLabels: { enabled: false },
    grid: {
      borderColor: "#e2e8f0",
      strokeDashArray: 5,
    },
    tooltip: {
      theme: "dark",
      y: {
        formatter: (value) => money(value),
      },
    },
    legend: {
      fontSize: "13px",
      labels: {
        colors: "#64748b",
      },
    },
  };

  const { setPageLoading } = useAppContext();
  useEffect(() => {
    setPageLoading(true);
  }, []);

  return (
    <div className="min-h-screen bg-blue-50">
      <div className="relative h-[50vh] min-h-[460px] overflow-hidden">
        <ImageContainer
          url="/images/success_firmly.jpg"
          className="absolute inset-0"
          onLoad={() => {
            setPageLoading(false);
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/55 to-slate-950" />
          <div className="absolute inset-0 backdrop-blur-[4px]" />

          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-center items-center">
            <TextSm className="uppercase tracking-[0.2em] text-blue-600 font-semibold select-none">
              Sales Performance
            </TextSm>

            <Text2xl className="font-bold text-white mt-4 select-none">
              Executive Sales Dashboard
            </Text2xl>

            <TextMd className="text-slate-300 mt-4 max-w-2xl leading-8 text-center select-none">
              A high-level view of revenue, profitability, product performance,
              customer segments, and recent order activity.
            </TextMd>
          </div>
        </ImageContainer>
      </div>

      <section className="relative z-20 max-w-7xl mx-auto px-6 lg:px-12 -mt-28 pb-16">
        <div className="rounded-[2rem] bg-slate-950/95 border border-white/10 shadow-2xl p-5 lg:p-6 mb-8 backdrop-blur-md">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Stat
              label="Total Sales"
              value={money(dashboard.totalSales, true)}
              sub="Gross revenue"
            />

            <Stat
              label="Total Profit"
              value={money(dashboard.totalProfit, true)}
              sub={`${(dashboard.profitMargin * 100).toFixed(1)}% margin`}
            />

            <Stat
              label="Units Sold"
              value={number(dashboard.totalQuantity)}
              sub="Total quantity"
            />

            <Stat
              label="Avg Discount"
              value={`${(dashboard.avgDiscount * 100).toFixed(1)}%`}
              sub="Average order discount"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8">
          <ChartPanel
            title="Quarterly Sales Trend"
            subtitle="Quarter-over-quarter sales performance."
          >
            <LazyChart
              type="area"
              height={380}
              series={[
                {
                  name: "Sales",
                  data: dashboard.quarterlySales.map((x) =>
                    Number(x.value.toFixed(2)),
                  ),
                },
              ]}
              options={{
                ...chartBase,
                stroke: {
                  curve: "smooth",
                  width: 4,
                  lineCap: "round",
                },
                markers: {
                  size: 0,
                  hover: { size: 6 },
                },
                fill: {
                  type: "gradient",
                  gradient: {
                    shade: "light",
                    type: "vertical",
                    gradientToColors: ["#06b6d4"],
                    opacityFrom: 0.45,
                    opacityTo: 0.03,
                    stops: [0, 90, 100],
                  },
                },
                xaxis: {
                  categories: dashboard.quarterlySales.map((x) => x.name),
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  min: 0,
                  forceNiceScale: true,
                  labels: {
                    formatter: (value) => `$${Math.round(value / 1000)}k`,
                  },
                },
              }}
            />
          </ChartPanel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <ChartPanel
              title="Sales by Region"
              subtitle="Regional revenue comparison."
            >
              <LazyChart
                type="bar"
                height={330}
                series={[
                  {
                    name: "Sales",
                    data: dashboard.byRegion.map((x) => x.value),
                  },
                ]}
                options={{
                  ...chartBase,
                  plotOptions: {
                    bar: {
                      borderRadius: 10,
                      columnWidth: "42%",
                    },
                  },
                  xaxis: {
                    categories: dashboard.byRegion.map((x) => x.name),
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                  },
                  yaxis: {
                    min: 0,
                    forceNiceScale: true,
                    labels: {
                      formatter: (value) => `$${Math.round(value / 1000)}k`,
                    },
                  },
                }}
              />
            </ChartPanel>

            <ChartPanel
              title="Sales by Category"
              subtitle="Category share of total sales."
            >
              <LazyChart
                type="donut"
                height={330}
                series={dashboard.byCategory.map((x) => x.value)}
                options={{
                  ...chartBase,
                  labels: dashboard.byCategory.map((x) => x.name),
                  stroke: {
                    width: 5,
                    colors: ["#ffffff"],
                  },
                  legend: {
                    position: "bottom",
                  },
                  plotOptions: {
                    pie: {
                      donut: {
                        size: "72%",
                        labels: {
                          show: true,
                          total: {
                            show: true,
                            label: "Total",
                            formatter: () => money(dashboard.totalSales, true),
                          },
                          value: {
                            formatter: (value) => money(value, true),
                          },
                        },
                      },
                    },
                  },
                }}
              />
            </ChartPanel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <ChartPanel
              title="Sales by Segment"
              subtitle="Customer segment contribution."
            >
              <LazyChart
                type="radialBar"
                height={330}
                series={dashboard.bySegment.map((x) =>
                  Number(((x.value / dashboard.totalSales) * 100).toFixed(1)),
                )}
                options={{
                  ...chartBase,
                  labels: dashboard.bySegment.map((x) => x.name),
                  plotOptions: {
                    radialBar: {
                      hollow: { size: "34%" },
                      track: { background: "#e2e8f0" },
                      dataLabels: {
                        value: {
                          fontSize: "18px",
                          fontWeight: 700,
                          color: "#0f172a",
                          formatter: (value) => `${value}%`,
                        },
                      },
                    },
                  },
                }}
              />
            </ChartPanel>

            <ChartPanel
              title="Top Sub-Categories"
              subtitle="Best performing product sub-categories."
              className="lg:col-span-2"
            >
              <LazyChart
                type="bar"
                height={330}
                series={[
                  {
                    name: "Sales",
                    data: dashboard.topSubCategories.map((x) => x.value),
                  },
                ]}
                options={{
                  ...chartBase,
                  plotOptions: {
                    bar: {
                      horizontal: true,
                      borderRadius: 10,
                      barHeight: "55%",
                    },
                  },
                  xaxis: {
                    min: 0,
                    forceNiceScale: true,
                    categories: dashboard.topSubCategories.map((x) => x.name),
                    labels: {
                      formatter: (value) => `$${Math.round(value / 1000)}k`,
                    },
                  },
                }}
              />
            </ChartPanel>
          </div>

          <ChartPanel
            title="Recent Orders"
            subtitle="Latest sales activity by order date."
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-200">
                    <th className="py-3 pr-6">Date</th>
                    <th className="py-3 pr-6">Customer</th>
                    <th className="py-3 pr-6">Region</th>
                    <th className="py-3 pr-6">Category</th>
                    <th className="py-3 pr-6 text-right">Sales</th>
                    <th className="py-3 pr-6 text-right">Profit</th>
                  </tr>
                </thead>

                <tbody>
                  {dashboard.recentOrders.map((order, index) => (
                    <tr
                      key={index}
                      className="border-b border-slate-100 hover:bg-slate-50 transition"
                    >
                      <td className="py-4 pr-6">{order["Order Date"]}</td>
                      <td className="py-4 pr-6">{order["Customer Name"]}</td>
                      <td className="py-4 pr-6">{order.Region}</td>
                      <td className="py-4 pr-6">{order.Category}</td>
                      <td className="py-4 pr-6 text-right font-semibold">
                        {money(order.Sales)}
                      </td>
                      <td className="py-4 pr-6 text-right font-semibold">
                        {money(order.Profit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartPanel>
        </div>
      </section>
    </div>
  );
};
